import { ruRU } from '@mui/material/locale';
import { createTheme } from '@mui/material/styles';
import { type ColorTokenKey, colorTokens, semanticPalette, type ThemeMode } from '@/design/tokens';
import { Mode } from '@/enums';

type CustomPalette = Record<ColorTokenKey, string>;

declare module '@mui/material/styles' {
    interface Theme {
        custom: CustomPalette;
    }
    interface ThemeOptions {
        custom: CustomPalette;
    }
    interface TypographyVariants {
        poster: React.CSSProperties;
        subtitle3: React.CSSProperties;
        subtitle4: React.CSSProperties;
    }
    interface TypographyVariantsOptions {
        poster?: React.CSSProperties;
        subtitle3?: React.CSSProperties;
        subtitle4?: React.CSSProperties;
    }
    interface BreakpointOverrides {
        elg: true;
    }
}
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        poster: true;
        subtitle3: true;
        subtitle4: true;
    }
}

export const theme = (mode: Mode) => {
    const modeKey: ThemeMode = mode === Mode.dark ? 'dark' : 'light';
    return createTheme(
        {
            palette: {
                mode,
                primary: {
                    main: semanticPalette.primary.main[modeKey],
                    light: semanticPalette.primary.light[modeKey],
                    contrastText: semanticPalette.primary.contrastText[modeKey],
                },
                error: {
                    main: semanticPalette.error.main[modeKey],
                    contrastText: semanticPalette.error.contrastText[modeKey],
                },
                secondary: {
                    main: semanticPalette.secondary.main[modeKey],
                    dark: semanticPalette.secondary.dark[modeKey],
                    light: semanticPalette.secondary.light[modeKey],
                    contrastText: semanticPalette.secondary.contrastText[modeKey],
                },
                info: {
                    main: semanticPalette.info.main[modeKey],
                    light: semanticPalette.info.light[modeKey],
                    dark: semanticPalette.info.dark[modeKey],
                    contrastText: semanticPalette.info.contrastText[modeKey],
                },
                text: {
                    primary: semanticPalette.text.primary[modeKey],
                    secondary: semanticPalette.text.secondary[modeKey],
                    disabled: semanticPalette.text.disabled[modeKey],
                },
                success: { main: semanticPalette.success.main[modeKey] },
                background: {
                    default: semanticPalette.background.default[modeKey],
                    paper: semanticPalette.background.paper[modeKey],
                },
            },

            custom: Object.fromEntries(
                (Object.keys(colorTokens) as ColorTokenKey[]).map(key => [key, colorTokens[key][modeKey]]),
            ) as CustomPalette,
            typography: {
                htmlFontSize: 16,
                fontSize: 16,
                fontFamily: ['Roboto Flex', 'Roboto Flex Static'].join(','),
                h1: {
                    fontSize: '2.62rem',
                },
                h2: {
                    fontSize: '2.62rem',
                },
                h4: {
                    fontSize: '2rem',
                    fontWeight: '600',
                },
                h5: {
                    fontSize: '2.5rem',
                    fontWeight: '700',
                },
                h6: {
                    fontSize: '0.75rem',
                    fontWeight: '400',
                },
                body1: {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                body2: {
                    fontSize: '0.875rem',
                    fontWeight: '400',
                },
                subtitle1: {
                    fontSize: '1.25rem',
                    fontWeight: '400',
                    lineHeight: '1.3',
                },
                subtitle2: {
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    fontWeight: '700',
                },
                subtitle3: {
                    fontSize: '1.125rem',
                },
                subtitle4: {
                    fontSize: '1.5rem',
                    fontWeight: '500',
                },
                poster: {
                    fontSize: '3rem',
                    lineHeight: '1.125',
                },
            },
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 900,
                    md: 1200,
                    lg: 1367,
                    elg: 1680,
                    xl: 1920,
                },
            },
            components: {
                MuiCssBaseline: {
                    defaultProps: {
                        enableColorScheme: true,
                    },
                    styleOverrides: {
                        '@font-face': {
                            fontFamily: 'Roboto Flex',
                            src: `url('/fonts/roboto/RobotoFlex-VariableFont.ttf') format('truetype')`,
                            fontWeight: '100 1000',
                            fontDisplay: 'block',
                        },
                        html: {
                            height: '100%',
                            body: {
                                minHeight: '100%',
                                '::-webkit-scrollbar': { display: 'none' },
                            },
                        },
                    },
                },
                MuiTypography: {
                    defaultProps: {
                        tabIndex: -1,
                        // `primary.main` is pure white in both modes (used elsewhere for
                        // buttons/icons on colored fills) — fine as the implicit text
                        // color on dark mode's dark surfaces, but invisible on light
                        // mode's light surfaces (issue #1799 redesign). Fall back to the
                        // theme's actual body text color in light mode instead.
                        color: mode === Mode.dark ? 'primary' : undefined,
                    },
                    styleOverrides: {
                        root: ({ theme, ownerState: { variant } }) => ({
                            outline: 'none',
                            textDecoration: 'none',
                            [theme.breakpoints.down('lg')]: {
                                ...(variant === 'h1' && {
                                    fontSize: '2.25rem',
                                }),
                                ...(variant === 'subtitle3' && {
                                    fontSize: '1rem',
                                }),
                                ...(variant === 'poster' && {
                                    fontSize: '2.25rem',
                                }),
                            },
                        }),
                    },
                },
                MuiModal: {
                    styleOverrides: {
                        root: ({ theme: _theme }) => ({
                            overflowY: 'scroll',
                            scrollbarWidth: 'none',
                            '::-webkit-scrollbar': {
                                width: '0',
                                height: '0',
                            },
                        }),
                    },
                },
                MuiInput: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            background: theme.palette.secondary.light,
                            color: mode === 'dark' ? theme.palette.primary.main : theme.custom.c56,
                            '& :-Webkit-autofill': {
                                WebkitBoxShadow: mode === 'dark' ? 'inset 0 0 0 50px #1b1a21' : 'inset 0 0 0 50px #e4e4e7',
                                WebkitTextFillColor: mode === 'dark' ? theme.palette.primary.main : theme.custom.c56,
                            },
                        }),
                    },
                    defaultProps: {
                        disableUnderline: true,
                    },
                },
                MuiSnackbar: {
                    styleOverrides: {
                        root: {
                            width: '360px',
                        },
                    },
                },
                MuiCheckbox: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            background: theme.palette.secondary.light,
                            color: theme.palette.primary.main,
                            border: `1px solid ${theme.palette.secondary.contrastText}`,
                            borderRadius: '12px',
                            padding: '6px',
                            '&.Mui-checked': {
                                background: theme.palette.secondary.main,
                                color: theme.palette.primary.main,
                                border: 'none',
                                borderRadius: '16px',
                            },
                        }),
                    },
                    defaultProps: {
                        disableRipple: true,
                    },
                },
                MuiCollapse: {
                    styleOverrides: {
                        wrapperInner: ({ theme: _theme }) => ({
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }),
                    },
                },
                MuiList: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            background: theme.palette.secondary.light,
                            color: mode === 'dark' ? theme.palette.primary.main : theme.custom.c27,
                            padding: '0',
                            '&:hover span': {
                                background: '#cecaf90f',
                            },
                        }),
                    },
                },
                MuiFormControl: {
                    styleOverrides: {
                        root: () => ({
                            maxWidth: '360px',
                            '&.MuiFormControl-fullWidth': {
                                maxWidth: 'none',
                            },
                        }),
                    },
                    defaultProps: {
                        fullWidth: true,
                    },
                },
                MuiButtonBase: {
                    defaultProps: {
                        disableRipple: true,
                        disableTouchRipple: true,
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            position: 'static',
                            transform: 'none',
                            marginTop: '15px',
                            marginBottom: '5px',
                            fontSize: '0.8rem',
                        },
                    },
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            color: theme.palette.error.contrastText,
                            margin: '7px 0 0 0',
                            padding: '0 5px',
                            height: 'auto',
                            fontSize: '0.88rem',
                            lineHeight: '1.4',
                        }),
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: {
                            border: '1px solid #cecaf90a',
                            margin: '16px 0',
                        },
                    },
                },
                MuiSlider: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            color: theme.palette.secondary.main,
                            maxWidth: '115px',
                        }),
                        thumbColorPrimary: {
                            boxShadow: 'none',
                            ':hover': {
                                boxShadow: 'none',
                            },
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            background: theme.custom.c24,
                        }),
                    },
                },
                MuiIconButton: {
                    defaultProps: {
                        disableRipple: true,
                    },
                },
                MuiSvgIcon: {
                    defaultProps: {
                        fontSize: 'small',
                    },
                    variants: [
                        {
                            props: { fontSize: 'small' },
                            style: ({ theme: _theme }) => ({
                                fontSize: '1rem',
                            }),
                        },
                        {
                            props: { fontSize: 'medium' },
                            style: ({ theme: _theme }) => ({
                                fontSize: '1.5rem',
                            }),
                        },
                    ],
                },
                MuiBackdrop: {
                    styleOverrides: {
                        root: {
                            zIndex: 4000,
                        },
                    },
                },
            },
        },
        ruRU,
    );
};
